import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  Feature,
  ListingGroupSettings,
  SellerAccountClient,
} from 'src/WebApiController';

import { useUserHasFeature } from '../useUserHasFeature';

export const useListingGroupSellerAccountSettings = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const hasListingGroupAccountSettingsFeature = useUserHasFeature(
    Feature.ListingGroupAccountSettings
  );

  const queryClient = useQueryClient();
  const queryKey = [
    'SellerAccountClient.getListingGroupSettings',
    activeAccountWebClientConfig.activeAccountId,
  ];

  const shouldQuery =
    activeAccountWebClientConfig.activeAccountId != null &&
    hasListingGroupAccountSettingsFeature;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).getListingGroupSettings();
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: async (settings: ListingGroupSettings) => {
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).mergeListingGroupSettings(settings);
    },
    onError: (err: ErrorTypes, settings) => {
      showErrorDialog('SellerAccountClient.mergeListingGroupSettings', err, {
        trackErrorData: { settings },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    mutation,
  };
};
